/* Extraído de index.jsx (refatoração 4.5) — abas e modais do plano alimentar.
   Código movido VERBATIM, sem mudança de lógica. */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { dietAIService, dietPlansService, patientsService, authService, foodDatabaseService, settingsService, pacienteDistribuicaoService } from '../../services/api';
import useIsMobile from '../../hooks/useIsMobile';
import { useToast } from '../../contexts/ToastContext';
import { SERVICES, STRATEGIES, sum, fmt1, dayTotals, guessCategory, getSubstitutes, serviceById, uniqueServices, strategyById, MEAL_ORDER, MEAL_ICONS, empty, ordenarRefeicoes } from './helpers';
import { RefeicoesConfig } from './refeicoes';
import { S, TotalsBar, MacroBlock, MacroPill, ServiceBadge, StrategyBadge, DataChip, Spinner, MealCard } from './ui';
import { UNIDADES, emptyAlimento, FoodSearchInput, MealTotals, DailyTotalsBar, TemplatesModal, getMicrosFromAlimento, SvgDonut, AnalysisPanel, emptyRefeicao } from './widgets';
import { Ico, emo, noEmo } from '../ui/Ico';
import { useMarca, LOGO_LEVNUTRI, logoScale } from '../../branding';

const escapeBrandHtml = value => String(value || '').replace(/[&<>"']/g, c =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const brandPrintHeader = marca => `<header style="display:flex;align-items:center;gap:16px;margin-bottom:20px"><img src="${escapeBrandHtml(marca.logo || LOGO_LEVNUTRI)}" alt="${escapeBrandHtml(marca.businessName || 'LevNutri')}" style="height:${56 * logoScale(marca.reportScale) / 100}px;max-width:100%;object-fit:contain"/><strong>${escapeBrandHtml(marca.businessName)}</strong></header>`;
const brandPrintFooter = marca => `<footer style="margin-top:24px;font-size:11px;color:#736b64">${marca.businessName ? `${escapeBrandHtml(marca.businessName)} · ` : ''}Gerado com LevNutri</footer>`;

export function ManualTab({ patientId, onSaved }) {
  const isMobile = useIsMobile();
  const [nomePlano,      setNomePlano]      = useState('');
  const [objetivo,       setObjetivo]       = useState('');
  const [validade,       setValidade]       = useState('');
  const [observacoes,    setObservacoes]    = useState('');
  const [refeicoes,      setRefeicoes]      = useState([emptyRefeicao()]);
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState('');
  const [showTemplates,  setShowTemplates]  = useState(false);
  const [daPaciente,     setDaPaciente]     = useState(false);

  // Plano ainda intocado → já começa com as refeições da distribuição da paciente (vazias)
  useEffect(() => {
    if (!patientId) return;
    let vivo = true;
    pacienteDistribuicaoService.get(patientId).then(r => {
      const dist = r?.success ? r.data.distribuicao : null;
      if (!vivo || !dist?.length) return;
      setRefeicoes(prev => {
        const intocado = prev.length === 1 && !prev[0].nome && !prev[0].horario && prev[0].alimentos.every(a => !a.nome);
        return intocado ? dist.map(d => ({ ...emptyRefeicao(), nome: d.nome, horario: d.horario || '' })) : prev;
      });
      setDaPaciente(true);
    }).catch(() => {});
    return () => { vivo = false; };
  }, [patientId]);

  const addRefeicao = () => setRefeicoes(prev => [...prev, emptyRefeicao()]);

  const removeRefeicao = (ri) =>
    setRefeicoes(prev => prev.filter((_, i) => i !== ri));

  const updateRefeicao = (ri, field, val) =>
    setRefeicoes(prev => prev.map((r, i) => i === ri ? { ...r, [field]: val } : r));

  const addAlimento = (ri) =>
    setRefeicoes(prev => prev.map((r, i) =>
      i === ri ? { ...r, alimentos: [...r.alimentos, emptyAlimento()] } : r));

  const removeAlimento = (ri, ai) =>
    setRefeicoes(prev => prev.map((r, i) =>
      i === ri ? { ...r, alimentos: r.alimentos.filter((_, j) => j !== ai) } : r));

  const updateAlimento = (ri, ai, patch) =>
    setRefeicoes(prev => prev.map((r, i) =>
      i === ri ? {
        ...r,
        alimentos: r.alimentos.map((a, j) => j === ai ? { ...a, ...patch } : a),
      } : r));

  // Called when a food is selected from the DB search
  const handleFoodSelect = (ri, ai, food) => {
    const qtd = parseFloat(refeicoes[ri]?.alimentos[ai]?.quantidade) || 100;
    const f   = qtd / 100;
    updateAlimento(ri, ai, {
      nome:         food.nome,
      calorias:     String(Math.round((food.calorias_100g || 0) * f)),
      proteina:     String(parseFloat(((food.proteina_100g || 0) * f).toFixed(1))),
      carboidratos: String(parseFloat(((food.carbo_100g   || 0) * f).toFixed(1))),
      gordura:      String(parseFloat(((food.gordura_100g || 0) * f).toFixed(1))),
      fibra:        String(parseFloat(((food.fibra_100g   || 0) * f).toFixed(1))),
      _per100g:     { calorias: food.calorias_100g, proteina: food.proteina_100g, carbo: food.carbo_100g, gordura: food.gordura_100g, fibra: food.fibra_100g || 0 },
    });
  };

  // When quantity changes on a DB-sourced food, recompute macros
  const handleQtdChange = (ri, ai, val) => {
    const al = refeicoes[ri]?.alimentos[ai];
    if (al?._per100g && al.unidade === 'g') {
      const f = (parseFloat(val) || 0) / 100;
      const p = al._per100g;
      updateAlimento(ri, ai, {
        quantidade:   val,
        calorias:     String(Math.round((p.calorias || 0) * f)),
        proteina:     String(parseFloat(((p.proteina || 0) * f).toFixed(1))),
        carboidratos: String(parseFloat(((p.carbo   || 0) * f).toFixed(1))),
        gordura:      String(parseFloat(((p.gordura || 0) * f).toFixed(1))),
        fibra:        String(parseFloat(((p.fibra   || 0) * f).toFixed(1))),
      });
    } else {
      updateAlimento(ri, ai, { quantidade: val });
    }
  };

  const applyTemplate = (template) => {
    setNomePlano(template.label);
    setRefeicoes(template.refeicoes.map(r => ({ ...r, alimentos: r.alimentos.map(a => ({ ...a })) })));
    setShowTemplates(false);
  };

  const totalCal  = refeicoes.flatMap(r => r.alimentos).reduce((s, a) => s + (parseFloat(a.calorias) || 0), 0);
  const totalProt = refeicoes.flatMap(r => r.alimentos).reduce((s, a) => s + (parseFloat(a.proteina) || 0), 0);
  const totalCarb = refeicoes.flatMap(r => r.alimentos).reduce((s, a) => s + (parseFloat(a.carboidratos) || 0), 0);
  const totalFat  = refeicoes.flatMap(r => r.alimentos).reduce((s, a) => s + (parseFloat(a.gordura) || 0), 0);

  const buildMealsPlan = () => {
    const meals = {};
    refeicoes.forEach((r, i) => {
      const key = `refeicao_${i + 1}`;
      meals[key] = {
        nome:             r.nome || `Refeição ${i + 1}`,
        horario_sugerido: r.horario,
        alimentos:        r.alimentos.map(a => ({
          alimento:     a.nome,
          porcao:       `${a.quantidade} ${a.unidade}`.trim(),
          calorias:     parseFloat(a.calorias)     || 0,
          proteina:     parseFloat(a.proteina)     || 0,
          carboidratos: parseFloat(a.carboidratos) || 0,
          gordura:      parseFloat(a.gordura)      || 0,
        })),
        calorias:     r.alimentos.reduce((s, a) => s + (parseFloat(a.calorias)     || 0), 0),
        proteina:     r.alimentos.reduce((s, a) => s + (parseFloat(a.proteina)     || 0), 0),
        carboidratos: r.alimentos.reduce((s, a) => s + (parseFloat(a.carboidratos) || 0), 0),
        gordura:      r.alimentos.reduce((s, a) => s + (parseFloat(a.gordura)      || 0), 0),
      };
    });
    return meals;
  };

  const handleSave = async () => {
    if (!nomePlano.trim()) { setError('Informe o nome do plano'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        patient_id:     patientId,
        plan_name:      nomePlano.trim(),
        daily_calories: Math.round(totalCal) || 0,
        protein_grams:  parseFloat(totalProt.toFixed(1)) || 0,
        carbs_grams:    parseFloat(totalCarb.toFixed(1)) || 0,
        fat_grams:      parseFloat(totalFat.toFixed(1))  || 0,
        ai_generated:   false,
        service_type:   '',
        strategy:       '',
        guidelines:     objetivo,
        notes:          observacoes,
        end_date:       validade || null,
        meals_plan:     buildMealsPlan(),
        suplementacao:  [],
      };
      const r = await dietPlansService.create(payload);
      if (r.success) { onSaved(); }
      else { setError(r.error || 'Erro ao salvar plano'); }
    } catch { setError('Erro ao salvar plano'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>

      {/* ── Coluna esquerda: conteúdo principal ── */}
      <div style={{ flex: 1, minWidth: 0 }}>

      {/* Cabeçalho */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#2f2a26' }}><Ico e="📝" sp />Criar Plano Alimentar Manual</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button style={{ ...S.outlineBtn, background: '#edf1f6', color: '#7189a6', fontWeight: 600 }}
            onClick={() => setShowTemplates(true)}>
            <Ico e="📋" sp />Usar Modelo
          </button>
          <button style={S.outlineBtn} onClick={() => window.print()}><Ico e="🖨" sp />Imprimir</button>
        </div>
      </div>

      {error && <div style={S.errorBox}>{error}</div>}

      {/* Informações gerais */}
      <div style={S.card}>
        <h3 style={S.cardTitle}>Informações do Plano</h3>
        <div style={S.grid2}>
          <div style={S.field}>
            <label style={S.label}>Nome do Plano *</label>
            <input style={S.input} value={nomePlano} onChange={e => setNomePlano(e.target.value)}
              placeholder="Ex: Plano Emagrecimento — Junho 2026" />
          </div>
          <div style={S.field}>
            <label style={S.label}>Validade (data fim)</label>
            <input type="date" style={S.input} value={validade} onChange={e => setValidade(e.target.value)} />
          </div>
        </div>
        <div style={{ marginTop: '1rem' }}>
          <label style={S.label}>Objetivo / Orientações gerais</label>
          <textarea style={S.textarea} rows={3} value={objetivo} onChange={e => setObjetivo(e.target.value)}
            placeholder="Descreva o objetivo do plano e orientações gerais..." />
        </div>
      </div>

      {/* Totais diários */}
      <DailyTotalsBar refeicoes={refeicoes} />

      {/* Refeições */}
      <div style={S.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ ...S.cardTitle, margin: 0 }}><Ico e="🍽" sp />Refeições</h3>
          <button style={S.addSupBtn} onClick={addRefeicao}>+ Adicionar Refeição</button>
        </div>
        {daPaciente && (
          <p style={{ margin: '-0.4rem 0 1rem', fontSize: '0.8rem', color: '#736b64' }}>
            Refeições iniciais vêm da distribuição salva para esta paciente. Você pode mudar à vontade.
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {refeicoes.map((ref, ri) => (
            <div key={ri} id={`refCard-${ri}`} style={{ border: '1.5px solid #ebe3da', borderRadius: '10px', padding: '1rem', background: '#fbf8f5' }}>
              {/* Cabeçalho da refeição */}
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', marginBottom: '0.75rem' }}>
                <div style={{ flex: 2 }}>
                  <label style={S.label}>Nome da Refeição</label>
                  <input style={S.input} value={ref.nome}
                    onChange={e => updateRefeicao(ri, 'nome', e.target.value)}
                    placeholder="Ex: Café da manhã, Almoço..." />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={S.label}>Horário</label>
                  <input style={S.input} value={ref.horario}
                    onChange={e => updateRefeicao(ri, 'horario', e.target.value)}
                    placeholder="07:00" />
                </div>
                {refeicoes.length > 1 && (
                  <button onClick={() => removeRefeicao(ri)}
                    style={{ padding: '0.5rem 0.75rem', background: '#f8ebe9', color: '#c46a60', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, flexShrink: 0, alignSelf: 'flex-end' }}>
                    <Ico e="🗑" sp />Remover
                  </button>
                )}
              </div>

              {/* Alimentos */}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                <thead>
                  <tr>
                    {['Alimento', 'Qtd.', 'Unidade', 'Kcal', 'Prot.g', 'Carb.g', 'Gord.g', ''].map(h => (
                      <th key={h} style={{ padding: '0.4rem 0.35rem', textAlign: 'left', background: '#fbf8f5', borderBottom: '1.5px solid #ebe3da', fontWeight: 600, color: '#736b64', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ref.alimentos.map((al, ai) => (
                    <tr key={ai}>
                      <td style={{ padding: '0.3rem 0.25rem', borderBottom: '1px solid #f6f0ea', minWidth: '160px' }}>
                        <FoodSearchInput
                          value={al.nome}
                          onChange={nome => updateAlimento(ri, ai, { nome })}
                          onSelect={food => handleFoodSelect(ri, ai, food)}
                          placeholder="Buscar alimento..."
                        />
                      </td>
                      <td style={{ padding: '0.3rem 0.25rem', borderBottom: '1px solid #f6f0ea' }}>
                        <input style={{ ...S.cellIn, width: '60px', textAlign: 'right' }} value={al.quantidade}
                          onChange={e => handleQtdChange(ri, ai, e.target.value)}
                          placeholder="100" />
                      </td>
                      <td style={{ padding: '0.3rem 0.25rem', borderBottom: '1px solid #f6f0ea' }}>
                        <select style={{ ...S.cellIn, minWidth: '75px' }} value={al.unidade}
                          onChange={e => updateAlimento(ri, ai, { unidade: e.target.value })}>
                          {UNIDADES.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '0.3rem 0.25rem', borderBottom: '1px solid #f6f0ea' }}>
                        <input type="number" min="0" style={{ ...S.cellIn, width: '60px', textAlign: 'right' }} value={al.calorias}
                          onChange={e => updateAlimento(ri, ai, { calorias: e.target.value, _per100g: null })}
                          placeholder="0" />
                      </td>
                      <td style={{ padding: '0.3rem 0.25rem', borderBottom: '1px solid #f6f0ea' }}>
                        <input type="number" min="0" step="0.1" style={{ ...S.cellIn, width: '55px', textAlign: 'right' }} value={al.proteina}
                          onChange={e => updateAlimento(ri, ai, { proteina: e.target.value })}
                          placeholder="0" />
                      </td>
                      <td style={{ padding: '0.3rem 0.25rem', borderBottom: '1px solid #f6f0ea' }}>
                        <input type="number" min="0" step="0.1" style={{ ...S.cellIn, width: '55px', textAlign: 'right' }} value={al.carboidratos}
                          onChange={e => updateAlimento(ri, ai, { carboidratos: e.target.value })}
                          placeholder="0" />
                      </td>
                      <td style={{ padding: '0.3rem 0.25rem', borderBottom: '1px solid #f6f0ea' }}>
                        <input type="number" min="0" step="0.1" style={{ ...S.cellIn, width: '55px', textAlign: 'right' }} value={al.gordura}
                          onChange={e => updateAlimento(ri, ai, { gordura: e.target.value })}
                          placeholder="0" />
                      </td>
                      <td style={{ padding: '0.3rem 0.25rem', borderBottom: '1px solid #f6f0ea' }}>
                        {ref.alimentos.length > 1 && (
                          <button onClick={() => removeAlimento(ri, ai)}
                            style={{ background: 'none', border: 'none', color: '#e8c9a8', cursor: 'pointer', fontSize: '0.9rem', padding: '2px 4px' }}><Ico e="✕" /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <button style={S.addFoodBtn} onClick={() => addAlimento(ri)}>+ Adicionar alimento</button>
              <MealTotals alimentos={ref.alimentos} />
            </div>
          ))}
        </div>
      </div>

      {/* Observações */}
      <div style={S.card}>
        <h3 style={S.cardTitle}><Ico e="📝" sp />Observações Gerais</h3>
        <textarea style={S.textarea} rows={4} value={observacoes} onChange={e => setObservacoes(e.target.value)}
          placeholder="Orientações complementares, restrições, dicas..." />
      </div>

      {/* Ações */}
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginBottom: '2rem' }}>
        <button style={S.outlineBtn} onClick={() => window.print()}><Ico e="🖨" sp />Imprimir / Exportar PDF</button>
        <button style={S.saveBtn} onClick={handleSave} disabled={saving}>
          {emo(saving ? 'Salvando...' : '💾 Salvar Plano')}
        </button>
      </div>

      {showTemplates && (
        <TemplatesModal onSelect={applyTemplate} onClose={() => setShowTemplates(false)} />
      )}

      </div>{/* fim coluna esquerda */}

      {/* ── Painel lateral (vira full-width no mobile) ── */}
      <div style={{ width: isMobile ? '100%' : '290px', flexShrink: 0 }}>
        <AnalysisPanel refeicoes={refeicoes} patientId={patientId} />
      </div>

    </div>
  );
}

/* ════════════════════════════════════════════════════════
   TAB: GERADOR
════════════════════════════════════════════════════════ */
/* ════════════════════════════════════════════════════════
   CARD: RESUMO CLÍNICO
════════════════════════════════════════════════════════ */
export function ClinicalSummaryCard({ summary, open, onToggle }) {
  const marca = useMarca();
  const GOLD   = '#d4a574';
  const BORDER = '#e8c9a8';
  const BG     = '#fbf8f5';
  const TEXT   = '#2f2a26';
  const SEC_TITLE = { fontSize: '0.8rem', fontWeight: 700, color: GOLD, textTransform: 'uppercase', letterSpacing: '.05em', margin: '0 0 4px' };
  const SEC_TEXT  = { fontSize: '0.88rem', color: TEXT, lineHeight: 1.6, margin: 0 };

  const buildPlainText = () => {
    const lines = ['📋 RESUMO CLÍNICO PARA A CONSULTA\n'];
    if (summary.justificativa_calorica) lines.push(`🔥 POR QUE ESTAS CALORIAS?\n${summary.justificativa_calorica}\n`);
    if (summary.escolha_alimentos)      lines.push(`🥗 POR QUE ESTES ALIMENTOS?\n${summary.escolha_alimentos}\n`);
    if (summary.distribuicao_macros)    lines.push(`⚖️ DISTRIBUIÇÃO DE MACROS\n${summary.distribuicao_macros}\n`);
    if (summary.suplementacao)          lines.push(`💊 SUPLEMENTAÇÃO\n${summary.suplementacao}\n`);
    if (summary.pontos_atencao?.length) lines.push(`⚠️ PONTOS DE ATENÇÃO\n${summary.pontos_atencao.map(p => `• ${p}`).join('\n')}\n`);
    if (summary.sugestoes_consulta?.length) lines.push(`💬 SUGESTÕES DE PERGUNTAS\n${summary.sugestoes_consulta.map(p => `• ${p}`).join('\n')}\n`);
    if (summary.previsao_resultados)    lines.push(`📈 PREVISÃO DE RESULTADOS\n${summary.previsao_resultados}`);
    return lines.join('\n');
  };

  const handlePrint = () => {
    const w = window.open('', '_blank', 'width=700,height=900');
    if (!w) return;
    // Texto vem da IA (influenciável pela anamnese do paciente) e a janela é da
    // mesma origem do painel: escapar evita XSS com acesso ao token de sessão.
    const esc = (v) => String(v ?? '').replace(/[&<>"']/g, c => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Resumo Clínico</title>
    <style>
      body{font-family:Georgia,serif;color:#2f2a26;padding:2rem;max-width:640px;margin:0 auto}
      h1{color:#d4a574;font-size:1.1rem;border-bottom:2px solid #e8c9a8;padding-bottom:.5rem}
      h2{color:#d4a574;font-size:.85rem;text-transform:uppercase;letter-spacing:.05em;margin:1.2rem 0 .3rem}
      p,li{font-size:.92rem;line-height:1.7;margin:.25rem 0}
      ul{padding-left:1.2rem}
      @media print{body{padding:1rem}}
    </style></head><body>
    ${brandPrintHeader(marca)}<h1>📋 Resumo Clínico para a Consulta</h1>`);
    const sections = [
      ['🔥 Por que estas calorias?', summary.justificativa_calorica],
      ['🥗 Por que estes alimentos?', summary.escolha_alimentos],
      ['⚖️ Distribuição de macros', summary.distribuicao_macros],
      ['💊 Suplementação', summary.suplementacao],
    ];
    sections.forEach(([title, text]) => {
      if (text) w.document.write(`<h2>${title}</h2><p>${esc(text)}</p>`);
    });
    if (summary.pontos_atencao?.length) {
      w.document.write(`<h2>⚠️ Pontos de atenção</h2><ul>${summary.pontos_atencao.map(p => `<li>${esc(p)}</li>`).join('')}</ul>`);
    }
    if (summary.sugestoes_consulta?.length) {
      w.document.write(`<h2>💬 Sugestões de perguntas</h2><ul>${summary.sugestoes_consulta.map(p => `<li>${esc(p)}</li>`).join('')}</ul>`);
    }
    if (summary.previsao_resultados) {
      w.document.write(`<h2>📈 Previsão de resultados</h2><p>${esc(summary.previsao_resultados)}</p>`);
    }
    w.document.write(`${brandPrintFooter(marca)}</body></html>`);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 400);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(buildPlainText()).catch(() => {});
  };

  const divider = { borderTop: `1px solid ${BORDER}`, margin: '12px 0' };
  const section = (icon, title, content) => !content ? null : (
    <div>
      <p style={SEC_TITLE}>{emo(icon)} {emo(title)}</p>
      <p style={SEC_TEXT}>{content}</p>
    </div>
  );

  return (
    <div className="no-print" style={{ background: BG, border: `1.5px solid ${BORDER}`, borderRadius: '12px', marginBottom: '1rem', overflow: 'hidden' }}>
      {/* Header clicável */}
      <div
        onClick={onToggle}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', cursor: 'pointer', borderBottom: open ? `1px solid ${BORDER}` : 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '1.1rem' }}><Ico e="📋" /></span>
          <div>
            <span style={{ fontWeight: 700, color: GOLD, fontSize: '0.9rem' }}>Resumo Clínico — Para usar na consulta</span>
            <span style={{ display: 'block', fontSize: '0.75rem', color: '#736b64', marginTop: '1px' }}>Gerado pela IA com base no perfil do paciente</span>
          </div>
        </div>
        <span style={{ color: GOLD, fontSize: '1.1rem', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>▾</span>
      </div>

      {open && (
        <div style={{ padding: '16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {emo(section('🔥', 'Por que estas calorias?', summary.justificativa_calorica))}
            {summary.justificativa_calorica && summary.escolha_alimentos && <div style={divider} />}
            {emo(section('🥗', 'Por que estes alimentos?', summary.escolha_alimentos))}
            {summary.escolha_alimentos && summary.distribuicao_macros && <div style={divider} />}
            {emo(section('⚖️', 'Distribuição de macros', summary.distribuicao_macros))}
            {summary.distribuicao_macros && summary.suplementacao && <div style={divider} />}
            {emo(section('💊', 'Suplementação', summary.suplementacao))}

            {summary.pontos_atencao?.length > 0 && (
              <>
                <div style={divider} />
                <div>
                  <p style={SEC_TITLE}><Ico e="⚠" sp />Pontos de atenção</p>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                    {summary.pontos_atencao.map((pt, i) => (
                      <li key={i} style={{ ...SEC_TEXT, marginBottom: '3px' }}>{pt}</li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {summary.sugestoes_consulta?.length > 0 && (
              <>
                <div style={divider} />
                <div>
                  <p style={SEC_TITLE}><Ico e="💬" sp />Sugestões de perguntas para a paciente</p>
                  <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                    {summary.sugestoes_consulta.map((q, i) => (
                      <li key={i} style={{ ...SEC_TEXT, marginBottom: '3px' }}>{q}</li>
                    ))}
                  </ul>
                </div>
              </>
            )}

            {summary.previsao_resultados && (
              <>
                <div style={divider} />
                {emo(section('📈', 'Previsão de resultados', summary.previsao_resultados))}
              </>
            )}
          </div>

          {/* Ações */}
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px', paddingTop: '12px', borderTop: `1px solid ${BORDER}` }}>
            <button
              onClick={handlePrint}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', background: GOLD, color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
            >
              <Ico e="🖨" sp />Imprimir resumo
            </button>
            <button
              onClick={handleCopy}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', background: '#ffffff', color: GOLD, border: `1.5px solid ${BORDER}`, borderRadius: '8px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
            >
              <Ico e="📋" sp />Copiar texto
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════ Validação do plano gerado × metas do programa ═══════════════════ */
const STATUS_VAL = {
  ok:     { icon: '✓', cor: '#2f5236', bg: '#edf4ee', label: 'OK' },
  alerta: { icon: '!', cor: '#8a5f1a', bg: '#fbf8f5', label: 'Revisar' },
  erro:   { icon: '✕', cor: '#a8483f', bg: '#f8ebe9', label: 'Corrigir' },
  info:   { icon: 'i', cor: '#4a4440', bg: '#fbf8f5', label: 'Info' },
};

export function ValidacaoPanel({ validacao, onClose }) {
  const [aberto, setAberto] = React.useState(true);
  const { checks = [], resumo = {}, metas, alertas = [], conflitos = [] } = validacao;
  const pend = (resumo.alerta || 0) + (resumo.erro || 0);
  const tom = resumo.erro ? STATUS_VAL.erro : pend ? STATUS_VAL.alerta : STATUS_VAL.ok;
  return (
    <section className="no-print" aria-label="Validação do plano gerado"
      style={{ border: `1.5px solid ${tom.cor}55`, background: '#ffffff', borderRadius: 12, marginBottom: '1rem', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 1rem', background: tom.bg }}>
        <button onClick={() => setAberto(a => !a)} aria-expanded={aberto}
          style={{ all: 'unset', cursor: 'pointer', flex: 1, display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <strong style={{ color: tom.cor, fontSize: '0.92rem' }}>
            {pend ? `Revise antes de salvar — ${pend} ponto(s) fora da meta` : 'Plano dentro das metas do programa'}
          </strong>
          <span style={{ fontSize: '0.78rem', color: '#736b64' }}>
            {resumo.ok || 0} ok · {resumo.alerta || 0} revisar · {resumo.erro || 0} corrigir {aberto ? '▲' : '▼'}
          </span>
        </button>
        <button onClick={onClose} aria-label="Fechar validação"
          style={{ background: 'none', border: 'none', color: '#7189a6', cursor: 'pointer', fontSize: '1rem' }}><Ico e="✕" /></button>
      </div>
      {aberto && (
        <div style={{ padding: '0.75rem 1rem', display: 'grid', gap: '0.5rem' }}>
          {metas && (
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#4a4440', lineHeight: 1.5 }}>
              <strong>Metas do programa:</strong> {metas.kcal} kcal ({metas.ajuste_pct > 0 ? '+' : ''}{metas.ajuste_pct}% sobre GET {metas.get} kcal, {metas.ajuste_origem})
              {' · '}proteína {emo(metas.proteina_faixa_g[0])}–{emo(metas.proteina_faixa_g[1])} g ({metas.proteina_g_kg} g/kg, {metas.peso_base_label})
              {' · '}gordura {emo(metas.gordura_faixa_g[0])}–{emo(metas.gordura_faixa_g[1])} g
              {metas.carbo_faixa_g && <> · carboidrato {emo(metas.carbo_faixa_g[0])}–{emo(metas.carbo_faixa_g[1])} g</>}
            </p>
          )}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.35rem' }}>
            {checks.map((c, i) => {
              const st = STATUS_VAL[c.status] || STATUS_VAL.info;
              return (
                <li key={i} style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start', fontSize: '0.83rem' }}>
                  <span aria-label={st.label} title={st.label}
                    style={{ flexShrink: 0, width: 20, height: 20, borderRadius: '50%', background: st.bg, color: st.cor,
                             border: `1px solid ${st.cor}55`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                             fontWeight: 700, fontSize: '0.72rem' }}>{emo(st.icon)}</span>
                  <span><strong style={{ color: '#34495e' }}>{c.item}:</strong> <span style={{ color: '#4a4440' }}>{c.detalhe}</span></span>
                </li>
              );
            })}
          </ul>
          {[...alertas, ...conflitos.map(c => `IA: ${c}`)].length > 0 && (
            <div style={{ background: '#fbf8f5', borderRadius: 8, padding: '0.6rem 0.8rem', fontSize: '0.8rem', color: '#5a3e2b' }}>
              {[...alertas, ...conflitos.map(c => `A IA apontou: ${c}`)].map((a, i) => <div key={i}><Ico e="⚠" /> {a}</div>)}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export function GeneratorTab({ patient, serviceType, setServiceType, strategy, setStrategy, generating, onGenerate, genderFocus,
                               refeicoes, setRefeicoes, refeicoesValidas, setRefeicoesValidas }) {
  const selected = serviceById(serviceType);

  /* Programas do SEXO DO PACIENTE (com opção de ver todos); sem paciente, segue a preferência da nutricionista */
  const [verTodos, setVerTodos] = React.useState(false);
  const sexoPaciente = patient?.gender === 'M' ? 'male' : patient?.gender === 'F' ? 'female' : null;
  const foco = verTodos ? 'mixed' : (sexoPaciente || genderFocus);
  const visibleServices = React.useMemo(() => {
    if (foco === 'mixed') return uniqueServices(SERVICES);
    return SERVICES.filter(s => s.gender === foco);
  }, [foco]);
  const selecionadoForaDoSexo = sexoPaciente && !SERVICES.some(s => s.id === serviceType && s.gender === sexoPaciente);

  /* Grupos para modo misto */
  const femaleServices = SERVICES.filter(s => s.gender === 'female');
  const maleServices   = SERVICES.filter(s => s.gender === 'male');

  /* Dados do paciente para summary */
  const imc = patient?.weight && patient?.height
    ? (patient.weight / Math.pow(patient.height < 3 ? patient.height : patient.height / 100, 2)).toFixed(1)
    : null;

  const imcLabel = (v) => {
    const n = parseFloat(v);
    if (n < 18.5) return { label: 'Abaixo do peso', color: '#7189a6' };
    if (n < 25)   return { label: 'Normal', color: '#46714f' };
    if (n < 30)   return { label: 'Sobrepeso', color: '#c4933f' };
    return { label: 'Obesidade', color: '#c46a60' };
  };

  const stepNumber = (base) => serviceType === 'planejamento_especializado' ? base + 1 : base;

  const renderServiceList = (list, keyPrefix) => list.map(svc => (
    <button
      key={keyPrefix + svc.id}
      style={{
        ...S.serviceCard,
        border: serviceType === svc.id
          ? `2.5px solid ${svc.color}`
          : '2px solid #ebe3da',
        background: serviceType === svc.id ? svc.bg : '#ffffff',
        boxShadow: serviceType === svc.id
          ? `0 4px 12px ${svc.color}30`
          : '0 1px 4px rgba(44,44,44,0.06)',
      }}
      onClick={() => { setServiceType(svc.id); setStrategy(''); }}
    >
      <span style={{ fontSize:'1.5rem' }}>{emo(svc.icon)}</span>
      <div>
        <div style={{ fontWeight:600, fontSize:'0.88rem', color: serviceType === svc.id ? svc.color : '#2f2a26' }}>
          {emo(svc.label)}
        </div>
        <div style={{ fontSize:'0.78rem', color:'#736b64', marginTop:'2px', lineHeight:1.4 }}>
          {emo(svc.desc)}
        </div>
      </div>
      {serviceType === svc.id && (
        <span style={{ marginLeft:'auto', color: svc.color, fontSize:'1rem' }}><Ico e="✓" /></span>
      )}
    </button>
  ));

  return (
    <div>
      {/* Resumo do Paciente */}
      {patient && (
        <div style={{ ...S.card, background: 'linear-gradient(135deg, #fbf8f5 0%, #fbf8f5 100%)', border: '1.5px solid #ebe3da' }}>
          <h2 style={{ ...S.cardTitle, marginBottom: '0.75rem' }}><Ico e="👤" /> {patient.name}</h2>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {patient.age && (
              <span style={{ background: '#ffffff', border: '1px solid #ebe3da', borderRadius: '20px', padding: '3px 10px', fontSize: '0.78rem', color: '#5a3e2b', fontWeight: 600 }}>
                <Ico e="🎂" /> {patient.age} anos
              </span>
            )}
            {patient.weight && (
              <span style={{ background: '#ffffff', border: '1px solid #ebe3da', borderRadius: '20px', padding: '3px 10px', fontSize: '0.78rem', color: '#5a3e2b', fontWeight: 600 }}>
                <Ico e="⚖" /> {patient.weight} kg
              </span>
            )}
            {patient.height && (
              <span style={{ background: '#ffffff', border: '1px solid #ebe3da', borderRadius: '20px', padding: '3px 10px', fontSize: '0.78rem', color: '#5a3e2b', fontWeight: 600 }}>
                <Ico e="📏" /> {patient.height < 3 ? (patient.height * 100).toFixed(0) : patient.height} cm
              </span>
            )}
            {imc && (() => { const info = imcLabel(imc); return (
              <span style={{ background: info.color + '18', border: `1px solid ${info.color}40`, borderRadius: '20px', padding: '3px 10px', fontSize: '0.78rem', color: info.color, fontWeight: 600 }}>
                IMC {imc} · {emo(info.label)}
              </span>
            ); })()}
            {patient.objective && (
              <span style={{ background: '#edf1f6', border: '1px solid #b8c7d8', borderRadius: '20px', padding: '3px 10px', fontSize: '0.78rem', color: '#4f6a86', fontWeight: 600 }}>
                <Ico e="🎯" /> {patient.objective?.replace(/_/g, ' ')}
              </span>
            )}
            {patient.is_lactose_intolerant && (
              <span style={{ background: '#f8f1e2', border: '1px solid #c4933f', borderRadius: '20px', padding: '3px 10px', fontSize: '0.78rem', color: '#8a5f1a', fontWeight: 600 }}>
                <Ico e="🥛" sp />Intolerante a lactose
              </span>
            )}
            {patient.is_vegan && (
              <span style={{ background: '#edf4ee', border: '1px solid #6f9a77', borderRadius: '20px', padding: '3px 10px', fontSize: '0.78rem', color: '#2f5236', fontWeight: 600 }}>
                <Ico e="🌿" sp />Vegano/a
              </span>
            )}
          </div>
        </div>
      )}

      {/* Seletor de Tipo de Serviço */}
      <div style={S.card}>
        <h2 style={S.cardTitle}>1. Tipo de Serviço</h2>
        <p style={{ color:'#736b64', fontSize:'0.875rem', marginBottom:'1rem' }}>
          Selecione o serviço que corresponde ao atendimento de <strong>{patient?.name}</strong>.
          As metas do programa (calorias, proteína, carboidrato e gordura) são calculadas pelo sistema e conferidas depois da geração.
        </p>

        {sexoPaciente && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: '#4a4440', marginBottom: '0.75rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={verTodos} onChange={e => setVerTodos(e.target.checked)} />
            Mostrar programas do outro sexo
          </label>
        )}
        {selecionadoForaDoSexo && (
          <p role="alert" style={{ background: '#fbf8f5', border: '1px solid #c4933f', borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: '#5a3e2b', margin: '0 0 0.75rem' }}>
            <Ico e="⚠" sp />O programa selecionado não é do sexo cadastrado do paciente. Escolha um programa da lista abaixo ou confirme que é intencional.
          </p>
        )}
        {foco === 'mixed' ? (
          <>
            <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#c46a60', textTransform: 'uppercase', letterSpacing: '.06em', margin: '0 0 0.5rem' }}>♀ Serviços Femininos</p>
            <div style={{ ...S.serviceGrid, marginBottom: '1rem' }}>
              {renderServiceList(femaleServices, 'f-')}
            </div>
            <p style={{ fontSize: '0.75rem', fontWeight: 600, color: '#4f6a86', textTransform: 'uppercase', letterSpacing: '.06em', margin: '0 0 0.5rem' }}>♂ Serviços Masculinos</p>
            <div style={S.serviceGrid}>
              {renderServiceList(maleServices, 'm-')}
            </div>
          </>
        ) : (
          <div style={S.serviceGrid}>
            {renderServiceList(visibleServices, '')}
          </div>
        )}
      </div>

      {/* Estratégia (só para Planejamento Especializado) */}
      {serviceType === 'planejamento_especializado' && (
        <div style={S.card}>
          <h2 style={S.cardTitle}>2. Estratégia Alimentar</h2>
          <p style={{ color:'#736b64', fontSize:'0.875rem', marginBottom:'1rem' }}>
            Escolha a abordagem que será seguida rigorosamente pelo plano gerado.
          </p>
          <div style={S.strategyGrid}>
            {STRATEGIES.map(st => (
              <button
                key={st.id}
                style={{
                  ...S.strategyCard,
                  border: strategy === st.id ? '2.5px solid #c4933f' : '2px solid #ebe3da',
                  background: strategy === st.id ? '#f6f0ea' : '#ffffff',
                  color: strategy === st.id ? '#c4933f' : '#4a4440',
                  fontWeight: strategy === st.id ? 700 : 500,
                }}
                onClick={() => setStrategy(st.id)}
              >
                <span style={{ fontSize:'1.3rem' }}>{emo(st.icon)}</span>
                {emo(st.label)}
                {strategy === st.id && <span style={{ marginLeft:'auto' }}><Ico e="✓" /></span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Refeições do plano (definidas pela nutricionista; metas calculadas no backend) */}
      <div style={S.card}>
        <h2 style={S.cardTitle}>{stepNumber(2)}. Refeições do plano</h2>
        <RefeicoesConfig
          patientId={patient?.id}
          serviceType={serviceType}
          strategy={strategy}
          value={refeicoes}
          onChange={setRefeicoes}
          onValidChange={setRefeicoesValidas}
        />
      </div>

      {/* Dados disponíveis + Botão gerar */}
      <div style={S.card}>
        <h2 style={S.cardTitle}>
          {stepNumber(3)}. Dados e Geração
        </h2>

        {/* Preview do serviço selecionado */}
        <div style={{
          display:'flex', alignItems:'center', gap:'0.75rem',
          padding:'0.75rem 1rem', borderRadius:'10px', marginBottom:'1.25rem',
          background: selected.bg, border: `1.5px solid ${selected.color}30`,
        }}>
          <span style={{ fontSize:'1.4rem' }}>{emo(selected.icon)}</span>
          <div>
            <div style={{ fontWeight:600, color: selected.color, fontSize:'0.9rem' }}>{emo(selected.label)}</div>
            {serviceType === 'planejamento_especializado' && strategy && (
              <div style={{ fontSize:'0.8rem', color:'#736b64' }}>
                Estratégia: {emo(strategyById(strategy)?.label)}
              </div>
            )}
          </div>
        </div>

        <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap', marginBottom:'1.5rem' }}>
          <DataChip label="Cadastro do Paciente" />
          <DataChip label="Bioimpedância OMRON" note="automático" />
          <DataChip label="Anamnese Clínica" note="automático" />
        </div>

        <button
          style={{ ...S.generateBtn, opacity: generating || !refeicoesValidas ? 0.6 : 1 }}
          onClick={onGenerate}
          disabled={generating || !refeicoesValidas}
          title={!refeicoesValidas ? 'Ajuste as refeições (a soma precisa ser 100%)' : undefined}
        >
          {emo(generating ? (
            <span style={{ display:'flex', alignItems:'center', gap:'0.75rem', justifyContent:'center' }}>
              <Spinner /> Gerando plano personalizado...
            </span>
          ) : `✨ Gerar Plano — ${selected.label}`)}
        </button>

        {generating && (
          <p style={{ color:'#736b64', fontSize:'0.85rem', marginTop:'0.75rem', textAlign:'center' }}>
            Montando {refeicoes?.length || ''} refeições para {patient?.name} com as metas de {selected.label.toLowerCase()}...
          </p>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   TAB: EDITOR
════════════════════════════════════════════════════════ */
export function EditorTab({ plan, setPlan, totals, updateFood, addFood, removeFood,
                      updateSupplement, addSupplement, removeSupplement, onSubstitute, onBrowseBank }) {
  return (
    <div>
      <TotalsBar totals={totals} water={plan.water_recommendation} aiModel={plan.ai_model} />

      {/* Meta do plano */}
      <div style={S.card} className="no-print">
        <div style={S.grid2}>
          <div style={S.field}>
            <label style={S.label}>Nome do Plano</label>
            <input style={S.input} value={plan.plan_name}
              onChange={e => setPlan(p => ({ ...p, plan_name: e.target.value }))}
              placeholder="Ex: Plano Emagrecimento — Abril 2026" />
          </div>
          <div style={S.field}>
            <label style={S.label}>Data de Início</label>
            <input type="date" style={S.input}
              value={plan.start_date || new Date().toISOString().split('T')[0]}
              onChange={e => setPlan(p => ({ ...p, start_date: e.target.value }))} />
          </div>
        </div>

        {plan.service_type && (
          <div style={{ marginTop:'0.75rem', display:'flex', gap:'0.5rem', flexWrap:'wrap', alignItems:'center' }}>
            <ServiceBadge serviceId={plan.service_type} />
            {plan.strategy && <StrategyBadge strategyId={plan.strategy} />}
            {plan.ai_generated && <span style={S.aiTag}><Ico e="✨" sp />Gerado por IA</span>}
          </div>
        )}
      </div>

      {/* Cards de refeições */}
      {ordenarRefeicoes(plan.meals).map(([mealKey]) => (
        <MealCard
          key={mealKey}
          mealKey={mealKey}
          meal={plan.meals[mealKey]}
          onUpdateFood={updateFood}
          onAddFood={addFood}
          onRemoveFood={removeFood}
          onSubstitute={onSubstitute}
          onBrowseBank={onBrowseBank}
        />
      ))}

      {/* ── Suplementação ── */}
      <SupplementsSection
        supplements={plan.supplements || []}
        onUpdate={updateSupplement}
        onAdd={addSupplement}
        onRemove={removeSupplement}
      />

      {/* Orientações */}
      <div style={S.card} className="guidelines-card">
        <h3 style={S.cardTitle}><Ico e="📌" sp />Orientações do Plano</h3>
        <textarea style={{ ...S.textarea, minHeight:'120px' }}
          value={plan.guidelines}
          onChange={e => setPlan(p => ({ ...p, guidelines: e.target.value }))}
          placeholder="Orientações gerais para o paciente..." />
      </div>

      {/* Notas internas */}
      <div style={S.card} className="no-print">
        <h3 style={S.cardTitle}><Ico e="📝" sp />Observações Internas</h3>
        <textarea style={S.textarea}
          value={plan.notes}
          onChange={e => setPlan(p => ({ ...p, notes: e.target.value }))}
          placeholder="Notas da nutricionista (não aparecem na impressão)..." />
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   SUPLEMENTAÇÃO
════════════════════════════════════════════════════════ */
export function SupplementsSection({ supplements, onUpdate, onAdd, onRemove }) {
  return (
    <div style={S.card}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
        <h3 style={{ ...S.cardTitle, margin:0 }}><Ico e="💊" sp />Suplementação Recomendada</h3>
        <button style={S.addSupBtn} onClick={onAdd}>+ Adicionar</button>
      </div>

      {supplements.length === 0 ? (
        <p style={{ color:'#736b64', fontSize:'0.88rem', margin:0, textAlign:'center', padding:'1rem 0' }}>
          Nenhum suplemento adicionado. Clique em "+ Adicionar" ou gere um plano com IA.
        </p>
      ) : (
        <div style={S.supGrid}>
          {supplements.map((sup, idx) => (
            <SupplementCard
              key={idx}
              sup={sup}
              idx={idx}
              onUpdate={onUpdate}
              onRemove={onRemove}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function SupplementCard({ sup, idx, onUpdate, onRemove }) {
  const hasObs = !!sup.observacao;
  return (
    <div style={S.supCard}>
      <div style={S.supCardTop}>
        <div style={{ flex:1, minWidth:0 }}>
          <input
            style={S.supNameIn}
            value={sup.nome}
            onChange={e => onUpdate(idx, 'nome', e.target.value)}
            placeholder="Nome do suplemento"
          />
        </div>
        <button style={S.supRemoveBtn} onClick={() => onRemove(idx)} title="Remover"><Ico e="✕" /></button>
      </div>

      <div style={S.supRow}>
        <div style={S.supField}>
          <span style={S.supFieldLbl}>Dosagem</span>
          <input style={S.supSmallIn} value={sup.dosagem}
            onChange={e => onUpdate(idx, 'dosagem', e.target.value)}
            placeholder="Ex: 500mg" />
        </div>
        <div style={{ ...S.supField, flex:2 }}>
          <span style={S.supFieldLbl}>Momento de uso</span>
          <input style={S.supSmallIn} value={sup.momento_uso}
            onChange={e => onUpdate(idx, 'momento_uso', e.target.value)}
            placeholder="Ex: Antes de dormir" />
        </div>
      </div>

      <div style={S.supField}>
        <span style={S.supFieldLbl}>Justificativa</span>
        <input style={S.supSmallIn} value={sup.justificativa}
          onChange={e => onUpdate(idx, 'justificativa', e.target.value)}
          placeholder="Motivo clínico..." />
      </div>

      <div style={S.supField}>
        <span style={S.supFieldLbl}>Observação</span>
        <input
          style={{ ...S.supSmallIn, background: hasObs ? '#fbf8f5' : undefined,
                   borderColor: hasObs ? '#c4933f' : undefined }}
          value={sup.observacao || ''}
          onChange={e => onUpdate(idx, 'observacao', e.target.value)}
          placeholder="Opcional — ex: Consultar médico antes de usar" />
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   TAB: HISTÓRICO
════════════════════════════════════════════════════════ */
export function HistoryTab({ patient, plans, loading, onOpen, onDelete, onNew, compareIds = [], onToggleCompare, onReactivate }) {
  const marca = useMarca();
  const comparePlans = plans.filter(p => compareIds.includes(p.id));

  return (
    <div style={S.card}>
      <h2 style={S.cardTitle}>
        Planos Alimentares Salvos{patient && ` — ${patient.name}`}
        {compareIds.length === 2 && (
          <button
            onClick={() => {
              /* exportar comparação como janela de impressão */
              const [a, b] = comparePlans;
              const w = window.open('', '_blank', 'width=800,height=600');
              if (!w) return;
              w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8">
                <title>Comparação de Planos</title>
                <style>
                  body{font-family:sans-serif;padding:24px;color:#2f2a26}
                  h2{color:#D4A574;margin-bottom:16px}
                  table{width:100%;border-collapse:collapse;margin-top:8px}
                  th,td{padding:10px 14px;border:1px solid #d9d0c6;text-align:left;font-size:13px}
                  th{background:#fbf8f5;font-weight:600;color:#5a3e2b}
                  tr:nth-child(even){background:#fbf8f5}
                  .up{color:#46714f;font-weight:600} .down{color:#c46a60;font-weight:600}
                  @media print{body{padding:12px}}
                </style></head><body>
                ${brandPrintHeader(marca)}<h2>Comparação de Planos Alimentares</h2>
                <table>
                  <thead><tr><th>Métrica</th><th>${a.plan_name}</th><th>${b.plan_name}</th></tr></thead>
                  <tbody>
                    <tr><td>Calorias/dia</td><td>${a.daily_calories} kcal</td><td>${b.daily_calories} kcal</td></tr>
                    <tr><td>Proteína</td><td>${a.protein_grams}g</td><td>${b.protein_grams}g</td></tr>
                    <tr><td>Carboidrato</td><td>${a.carbs_grams}g</td><td>${b.carbs_grams}g</td></tr>
                    <tr><td>Gordura</td><td>${a.fat_grams}g</td><td>${b.fat_grams}g</td></tr>
                    <tr><td>Tipo de Serviço</td><td>${a.service_type || '-'}</td><td>${b.service_type || '-'}</td></tr>
                    <tr><td>Status</td><td>${a.status}</td><td>${b.status}</td></tr>
                  </tbody>
                </table>
                ${brandPrintFooter(marca)}
              </body></html>`);
              w.document.close();
              setTimeout(() => w.print(), 400);
            }}
            style={{ ...S.secondaryBtn, fontSize:'0.8rem', padding:'0.3rem 0.8rem', marginLeft:'auto' }}
          >
            <Ico e="📄" sp />Exportar comparação
          </button>
        )}
      </h2>

      {loading ? (
        <p style={S.emptyMsg}>Carregando...</p>
      ) : plans.length === 0 ? (
        <div style={S.emptyState}>
          <p>Nenhum plano salvo ainda.</p>
          <button style={S.primaryBtn} onClick={onNew}><Ico e="✨" sp />Gerar primeiro plano com IA</button>
        </div>
      ) : (
        <>
          {compareIds.length > 0 && (
            <div style={{ marginBottom:'0.75rem', padding:'0.5rem 0.75rem', background:'#fbf8f5', borderRadius:'8px', fontSize:'0.83rem', color:'#5a3e2b', border:'1px solid #e8c9a8', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span>{compareIds.length === 1 ? '1 plano selecionado — escolha mais um para comparar' : '2 planos selecionados — comparação abaixo ↓'}</span>
            </div>
          )}
          <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
            {plans.map(p => (
              <HistoryItem
                key={p.id} plan={p}
                onOpen={onOpen} onDelete={onDelete}
                onToggleCompare={onToggleCompare} onReactivate={onReactivate}
                selected={compareIds.includes(p.id)}
              />
            ))}
          </div>
          {compareIds.length === 2 && <ComparisonView plans={comparePlans} />}
        </>
      )}
    </div>
  );
}

export function HistoryItem({ plan, onOpen, onDelete, onToggleCompare, onReactivate, selected }) {
  const isMobile = useIsMobile();
  // Macros numa linha horizontal compacta no mobile (rola na horizontal se faltar espaço)
  const metaStyle = isMobile
    ? { ...S.historyMeta, gap:'0.6rem', fontSize:'0.74rem', flexWrap:'nowrap',
        overflowX:'auto', WebkitOverflowScrolling:'touch', whiteSpace:'nowrap' }
    : S.historyMeta;
  const nameStyle = isMobile
    ? { ...S.historyName, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', maxWidth:'100%' }
    : S.historyName;

  return (
    <div style={{ ...S.historyItem, outline: selected ? '2px solid #D4A574' : 'none', outlineOffset:'1px' }}>
      <div style={{ flex:1, minWidth:0, width: isMobile ? '100%' : undefined }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', flexWrap:'wrap', marginBottom:'0.4rem' }}>
          <span style={nameStyle}>{plan.plan_name}</span>
          {plan.service_type && <ServiceBadge serviceId={plan.service_type} small />}
          {plan.strategy     && <StrategyBadge strategyId={plan.strategy} small />}
          {plan.ai_generated ? <span style={S.aiTag}><Ico e="✨" sp />IA</span> : null}
          <span style={{
            ...S.statusTag,
            background: plan.status === 'ativo' ? '#f6f0ea' : '#fbf8f5',
            color:       plan.status === 'ativo' ? '#46714f' : '#736b64',
          }}>{emo(plan.status)}</span>
        </div>
        <div style={metaStyle}>
          <span><Ico e="📅" /> {new Date(plan.start_date + 'T12:00:00').toLocaleDateString('pt-BR')}</span>
          <span><Ico e="🔥" /> {plan.daily_calories} kcal</span>
          <span><Ico e="💪" /> {fmt1(plan.protein_grams)}g prot</span>
          <span><Ico e="🌾" /> {fmt1(plan.carbs_grams)}g carb</span>
          <span><Ico e="🥑" /> {fmt1(plan.fat_grams)}g gord</span>
        </div>
      </div>
      <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap',
        justifyContent: isMobile ? 'stretch' : 'flex-end',
        width: isMobile ? '100%' : undefined }}>
        {onToggleCompare && (
          <button
            style={{ ...S.secondaryBtn, fontSize:'0.8rem', padding:'0.45rem 0.7rem',
              flex: isMobile ? '1 1 45%' : undefined,
              background: selected ? '#f5ece2' : undefined,
              border: selected ? '1px solid #D4A574' : '1px solid #d9d0c6',
              color: selected ? '#9a6a42' : undefined }}
            onClick={() => onToggleCompare(plan.id)}
          >
            {emo(selected ? '☑ Comparar' : '⊡ Comparar')}
          </button>
        )}
        {onReactivate && plan.status !== 'ativo' && (
          <button style={{ ...S.secondaryBtn, fontSize:'0.8rem', padding:'0.45rem 0.7rem',
            flex: isMobile ? '1 1 45%' : undefined }}
            onClick={() => onReactivate(plan.id)}>
            ▶ Reativar
          </button>
        )}
        <button style={{ ...S.openBtn, flex: isMobile ? '1 1 45%' : undefined }} onClick={() => onOpen(plan.id)}><Ico e="✏" sp />Abrir</button>
        <button style={{ ...S.delBtn, flex: isMobile ? '0 0 auto' : undefined, minWidth: isMobile ? '48px' : undefined }} onClick={() => onDelete(plan.id)}><Ico e="🗑" /></button>
      </div>
    </div>
  );
}

export function ComparisonView({ plans }) {
  if (plans.length < 2) return null;
  const [a, b] = plans;

  const rows = [
    { label: 'Calorias/dia', va: a.daily_calories, vb: b.daily_calories, unit: ' kcal' },
    { label: 'Proteína (g)', va: fmt1(a.protein_grams), vb: fmt1(b.protein_grams), unit: 'g', num: true, rawA: +a.protein_grams, rawB: +b.protein_grams },
    { label: '% Proteína',   va: a.protein_grams && a.daily_calories ? fmt1((a.protein_grams * 4 / a.daily_calories) * 100) : '-',
                              vb: b.protein_grams && b.daily_calories ? fmt1((b.protein_grams * 4 / b.daily_calories) * 100) : '-', unit: '%' },
    { label: 'Carboidrato (g)', va: fmt1(a.carbs_grams), vb: fmt1(b.carbs_grams), unit: 'g', num: true, rawA: +a.carbs_grams, rawB: +b.carbs_grams },
    { label: 'Gordura (g)',  va: fmt1(a.fat_grams), vb: fmt1(b.fat_grams), unit: 'g', num: true, rawA: +a.fat_grams, rawB: +b.fat_grams },
    { label: 'Tipo de serviço', va: a.service_type || '-', vb: b.service_type || '-' },
    { label: 'Início',       va: new Date(a.start_date+'T12:00:00').toLocaleDateString('pt-BR'), vb: new Date(b.start_date+'T12:00:00').toLocaleDateString('pt-BR') },
  ];

  return (
    <div style={{ marginTop:'1rem', borderTop:'2px solid #ebe3da', paddingTop:'1rem' }}>
      <h3 style={{ fontSize:'0.95rem', fontWeight:600, color:'#5a3e2b', marginBottom:'0.75rem' }}>
        Comparação de Planos
      </h3>
      <div style={{ overflowX:'auto' }}>
        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.85rem' }}>
          <thead>
            <tr>
              <th style={{ textAlign:'left', padding:'8px 10px', background:'#fbf8f5', color:'#9a6a42', borderBottom:'2px solid #e8c9a8', fontWeight:600 }}>Métrica</th>
              <th style={{ textAlign:'center', padding:'8px 10px', background:'#fbf8f5', color:'#9a6a42', borderBottom:'2px solid #e8c9a8', fontWeight:600, maxWidth:'180px' }}>{a.plan_name}</th>
              <th style={{ textAlign:'center', padding:'8px 10px', background:'#fbf8f5', color:'#9a6a42', borderBottom:'2px solid #e8c9a8', fontWeight:600, maxWidth:'180px' }}>{b.plan_name}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              let colorA = '', colorB = '';
              if (row.num) {
                if (row.rawA > row.rawB) { colorA = '#46714f'; colorB = '#c46a60'; }
                else if (row.rawA < row.rawB) { colorA = '#c46a60'; colorB = '#46714f'; }
              }
              return (
                <tr key={i} style={{ background: i % 2 === 0 ? '#ffffff' : '#fbf8f5' }}>
                  <td style={{ padding:'8px 10px', color:'#4a4440', fontWeight:500, borderBottom:'1px solid #f6f0ea' }}>{emo(row.label)}</td>
                  <td style={{ padding:'8px 10px', textAlign:'center', borderBottom:'1px solid #f6f0ea', color: colorA || '#2f2a26', fontWeight: colorA ? 700 : 400 }}>
                    {row.va}{row.unit && typeof row.va === 'number' ? row.unit : ''}
                  </td>
                  <td style={{ padding:'8px 10px', textAlign:'center', borderBottom:'1px solid #f6f0ea', color: colorB || '#2f2a26', fontWeight: colorB ? 700 : 400 }}>
                    {row.vb}{row.unit && typeof row.vb === 'number' ? row.unit : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   FOOD DATABASE PANEL
════════════════════════════════════════════════════════ */
export function FoodDatabasePanel({
  isMobile, onClose,
  mode = 'add', currentName, targetMealName,
  tab, onTabChange, foods, supplements, categories, supCategories,
  category, onCategoryChange, supCategory, onSupCategoryChange,
  search, onSearchChange, dbLoaded,
  onAddFood, onAddSupplement,
}) {
  const isSub = mode === 'substitute';
  const title = isSub
    ? 'Substituir alimento'
    : (targetMealName ? `Adicionar — ${targetMealName}` : 'Banco de Alimentos');
  // Banner de destaque quando estiver substituindo
  const subBanner = isSub && (
    <div style={{ background:'#f5ece2', border:'1px solid #e3c48a', borderRadius:'8px',
      padding:'0.5rem 0.7rem', marginBottom:'0.6rem', fontSize:'0.82rem', color:'#5a3e2b', flexShrink:0 }}>
      <Ico e="🔄" sp />Substituindo: <strong>{currentName}</strong>
    </div>
  );
  const panelStyle = isMobile
    ? { ...S.foodPanel, width:'100%', maxWidth:'100%', height:'70vh', maxHeight:'70vh', position:'static',
        borderRadius:'18px 18px 0 0', boxShadow:'0 -8px 32px rgba(0,0,0,0.22)', paddingTop:'0.4rem' }
    : S.foodPanel;

  const panel = (
    <div style={panelStyle}>
      {/* Cabeçalho mobile: drag handle + título + botão fechar (fixos, não rolam) */}
      {isMobile && (
        <div style={{ flexShrink:0 }}>
          {/* Drag handle */}
          <div style={{ width:'40px', height:'4px', borderRadius:'4px', background:'#d9d0c6', margin:'0 auto 0.6rem' }} />
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.4rem' }}>
            <strong style={{ fontSize:'1rem', color: isSub ? '#5a3e2b' : '#5a3e2b' }}>
              {emo(isSub ? '🔄 Substituir alimento' : `🥩 ${title}`)}
            </strong>
            <button onClick={onClose} aria-label="Fechar"
              style={{ width:40, height:40, border:'none', background:'rgba(212,165,116,0.12)', borderRadius:'50%', fontSize:'1.1rem', color:'#B8895C', cursor:'pointer', flexShrink:0 }}>
              <Ico e="✕" />
            </button>
          </div>
          {subBanner}
          {!isSub && (
            <p style={{ margin:'0 0 0.6rem', fontSize:'0.76rem', color:'#736b64' }}>
              Toque em <Ico e="➕" sp />para escolher a porção e adicionar.
            </p>
          )}
        </div>
      )}
      {/* Cabeçalho desktop: título + modo + fechar */}
      {!isMobile && (
        <div style={{ flexShrink:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.5rem' }}>
            <strong style={{ fontSize:'0.92rem', color: isSub ? '#5a3e2b' : '#5a3e2b' }}>
              {emo(isSub ? '🔄 Substituir' : `🥩 ${title}`)}
            </strong>
            <button onClick={onClose} aria-label="Fechar"
              style={{ width:28, height:28, border:'none', background:'rgba(212,165,116,0.12)', borderRadius:'50%', fontSize:'0.9rem', color:'#B8895C', cursor:'pointer', flexShrink:0 }}>
              <Ico e="✕" />
            </button>
          </div>
          {subBanner}
        </div>
      )}
      {/* Tabs (ocultas no modo substituir — só alimentos) */}
      {!isSub && (
        <div style={{ display:'flex', borderBottom:'2px solid #ebe3da', marginBottom:'0.75rem' }}>
          {emo([['foods','🥩 Alimentos'],['supplements','💊 Suplementos']].map(([t, lbl]) => (
            <button key={t} onClick={() => { onTabChange(t); onSearchChange(''); }}
              style={{ flex:1, padding:'0.5rem', border:'none', background:'none', cursor:'pointer',
                fontWeight: tab === t ? 700 : 500,
                color: tab === t ? '#D4A574' : '#736b64',
                borderBottom: tab === t ? '2.5px solid #D4A574' : '2.5px solid transparent',
                fontSize:'0.82rem', marginBottom:'-2px' }}>
              {emo(lbl)}
            </button>
          )))}
        </div>
      )}

      {/* Search */}
      <input
        style={{ ...S.input, marginBottom:'0.6rem', fontSize:'0.84rem', padding:'0.5rem 0.75rem' }}
        placeholder={tab === 'foods' ? 'Buscar alimento...' : 'Buscar suplemento...'}
        value={search}
        onChange={e => onSearchChange(e.target.value)}
      />

      {/* Category filter — mais contraste e área de toque maior no mobile */}
      {(() => {
        const catSelect = {
          ...S.select, maxWidth:'100%', marginBottom:'0.6rem',
          fontSize: isMobile ? '0.92rem' : '0.83rem',
          padding: isMobile ? '0.65rem 0.85rem' : '0.45rem 0.7rem',
          border: '1.5px solid #D4A574', background:'#ffffff',
          color:'#5a3e2b', fontWeight: 600,
        };
        return tab === 'foods' ? (
          <select style={catSelect}
            value={category} onChange={e => onCategoryChange(e.target.value)}>
            <option value="">Todas as categorias</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        ) : (
          <select style={catSelect}
            value={supCategory} onChange={e => onSupCategoryChange(e.target.value)}>
            <option value="">Todas as categorias</option>
            {supCategories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        );
      })()}

      {/* Items list */}
      <div style={{ overflowY:'auto', flex:1, display:'flex', flexDirection:'column', gap:'0.4rem' }}>
        {!dbLoaded ? (
          <p style={{ color:'#a39b93', fontSize:'0.83rem', textAlign:'center', padding:'1.5rem 0' }}>
            Carregando banco de dados...
          </p>
        ) : tab === 'foods' ? (
          foods.length === 0 ? (
            <p style={{ color:'#a39b93', fontSize:'0.83rem', textAlign:'center', padding:'1rem 0' }}>Nenhum alimento encontrado</p>
          ) : foods.map(food => (
            <FoodPanelItem key={food.id} item={food} onAdd={() => onAddFood(food)} substitute={isSub} />
          ))
        ) : (
          supplements.length === 0 ? (
            <p style={{ color:'#a39b93', fontSize:'0.83rem', textAlign:'center', padding:'1rem 0' }}>Nenhum suplemento encontrado</p>
          ) : supplements.map(sup => (
            <SupplementPanelItem key={sup.id} item={sup} onAdd={() => onAddSupplement(sup)} />
          ))
        )}
      </div>
    </div>
  );

  // No mobile, o painel vira um bottom-sheet sobre overlay, ocupando a largura total.
  if (isMobile) {
    return (
      <div
        onClick={onClose}
        style={{ position:'fixed', inset:0, zIndex:1200, background:'rgba(0,0,0,0.3)',
                 display:'flex', alignItems:'flex-end' }}
      >
        <div onClick={e => e.stopPropagation()} style={{ width:'100%' }}>
          {panel}
        </div>
      </div>
    );
  }

  return panel;
}

const FONTE_SELO = {
  TACO: { txt: '🇧🇷 TACO', bg: '#f6f0ea', cor: '#46714f' },
  USDA: { txt: '🇺🇸 USDA', bg: '#edf1f6', cor: '#4f6a86' },
};

export function FoodPanelItem({ item, onAdd, substitute }) {
  const selo = FONTE_SELO[item.source] || null;
  return (
    <div style={S.fpItem}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'2px' }}>
          <span style={{ fontWeight:600, fontSize:'0.83rem', color:'#2f2a26',
            overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{emo(item.nome)}</span>
          {selo && (
            <span style={{ flexShrink:0, fontSize:'0.62rem', fontWeight:600, background:selo.bg, color:selo.cor,
              padding:'1px 6px', borderRadius:'20px', lineHeight:1.5 }}>{selo.txt}</span>
          )}
        </div>
        <div style={{ fontSize:'0.72rem', color:'#736b64' }}>
          <span style={{ color:'#D4A574', fontWeight:600 }}>{Math.round(item.calorias_100g)} kcal</span>
          {' · '}P {item.proteina_100g}g
          {' · '}C {item.carbo_100g}g
          {' · '}G {item.gordura_100g}g
          {' — '}<span style={{ color:'#a39b93' }}>/100g</span>
        </div>
        <div style={{ fontSize:'0.7rem', color:'#d9d0c6' }}>
          Porção ref.: {item.porcao_padrao}g ({item.unidade_padrao})
        </div>
      </div>
      {substitute ? (
        <button
          style={{ ...S.fpAddBtn, width:'auto', padding:'0 0.7rem', fontSize:'0.8rem', fontWeight:600,
            background:'#46714f', whiteSpace:'nowrap' }}
          onClick={onAdd} title="Usar este alimento como substituto"><Ico e="✓" sp />Usar</button>
      ) : (
        <button style={S.fpAddBtn} onClick={onAdd} title="Adicionar ao plano">+</button>
      )}
    </div>
  );
}

export function SupplementPanelItem({ item, onAdd }) {
  return (
    <div style={S.fpItem}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:600, fontSize:'0.83rem', color:'#2f2a26', marginBottom:'2px',
          overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{emo(item.nome)}</div>
        <div style={{ fontSize:'0.72rem', color:'#7189a6', fontWeight:600 }}>
          {item.dosagem_padrao} {item.unidade}
        </div>
        <div style={{ fontSize:'0.7rem', color:'#736b64', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {item.momento_uso}
        </div>
      </div>
      <button style={{ ...S.fpAddBtn, background:'#7189a6' }} onClick={onAdd} title="Adicionar à suplementação">+</button>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   ADD FOOD MODAL
════════════════════════════════════════════════════════ */
export function AddFoodModal({ food, mode = 'add', currentName, mealKeys, mealNames, selectedMeal, onMealChange, portion, onPortionChange, onConfirm, onClose }) {
  const isSub   = mode === 'substitute';
  const f       = portion / 100;
  const cal     = Math.round((food.calorias_100g || 0) * f);
  const prot    = ((food.proteina_100g || 0) * f).toFixed(1);
  const carb    = ((food.carbo_100g    || 0) * f).toFixed(1);
  const fat     = ((food.gordura_100g  || 0) * f).toFixed(1);
  const canAdd  = selectedMeal && portion > 0;

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={S.modalBox} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
          <h3 style={{ margin:0, fontSize:'1rem', fontWeight:600, color:'#2f2a26' }}>
            {isSub ? 'Substituir alimento' : 'Adicionar ao Plano'}
          </h3>
          <button style={S.modalClose} onClick={onClose}><Ico e="✕" /></button>
        </div>
        {isSub && (
          <div style={{ background:'#f5ece2', border:'1px solid #e3c48a', borderRadius:'8px',
            padding:'0.5rem 0.7rem', marginBottom:'0.75rem', fontSize:'0.83rem', color:'#5a3e2b' }}>
            <Ico e="🔄" sp />Substituindo <strong>{currentName}</strong> em <strong>{emo(mealNames[selectedMeal] || selectedMeal)}</strong>
          </div>
        )}
        <p style={{ margin:'0 0 1rem', fontWeight:600, color:'#D4A574', fontSize:'0.95rem' }}>
          {emo(isSub ? `Novo: ${food.nome}` : food.nome)}
        </p>

        {!isSub && (
          <div style={{ marginBottom:'0.75rem' }}>
            <label style={S.label}>Refeição destino</label>
            <select style={{ ...S.select, maxWidth:'100%' }} value={selectedMeal} onChange={e => onMealChange(e.target.value)}>
              <option value="">Selecione a refeição...</option>
              {mealKeys.map(k => <option key={k} value={k}>{noEmo(mealNames[k] || k)}</option>)}
            </select>
          </div>
        )}

        <div style={{ marginBottom:'1rem' }}>
          <label style={S.label}>Porção (g)</label>
          <input type="number" min="1" max="2000" style={S.input} value={portion}
            onChange={e => onPortionChange(Math.max(1, parseInt(e.target.value) || 1))} />
        </div>

        {/* Calculated macros */}
        <div style={{ background:'#fbf8f5', borderRadius:'8px', padding:'0.75rem',
          display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'0.5rem', marginBottom:'1.25rem',
          textAlign:'center' }}>
          {[['Kcal', cal, '#D4A574'], ['Prot.', `${prot}g`, '#7189a6'], ['Carb.', `${carb}g`, '#b9d2bd'], ['Gord.', `${fat}g`, '#e8c9a8']].map(([l,v,c]) => (
            <div key={l}>
              <div style={{ fontSize:'0.7rem', color:'#a39b93' }}>{l}</div>
              <div style={{ fontWeight:600, color:c, fontSize:'0.95rem' }}>{v}</div>
            </div>
          ))}
        </div>

        <div style={{ display:'flex', gap:'0.5rem' }}>
          <button style={{ ...S.outlineBtn, flex:1 }} onClick={onClose}>Cancelar</button>
          <button style={{ ...S.saveBtn, flex:2, opacity: canAdd ? 1 : 0.5,
            ...(isSub ? { background:'#46714f' } : {}) }}
            onClick={onConfirm} disabled={!canAdd}>
            {emo(isSub ? '✓ Substituir' : '+ Adicionar à Refeição')}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════
   SUBSTITUTE MODAL
════════════════════════════════════════════════════════ */
export function SubstituteModal({ food, substitutes, onSelect, onBrowse, onClose }) {
  const [selPortion, setSelPortion] = useState({});

  return (
    <div style={S.modalOverlay} onClick={onClose}>
      <div style={{ ...S.modalBox, maxWidth:'520px' }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.75rem' }}>
          <h3 style={{ margin:0, fontSize:'1rem', fontWeight:600, color:'#2f2a26' }}>
            <Ico e="🔄" sp />Substituições para
          </h3>
          <button style={S.modalClose} onClick={onClose}><Ico e="✕" /></button>
        </div>
        <p style={{ margin:'0 0 1rem', fontWeight:600, color:'#D4A574' }}>
          {food.alimento} — {food.porcao} ({Math.round(food.calorias)} kcal)
        </p>

        {substitutes.length === 0 ? (
          <p style={{ color:'#736b64', textAlign:'center', padding:'1rem 0', fontSize:'0.88rem' }}>
            Nenhuma substituição encontrada no banco de dados para este alimento.
          </p>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem', marginBottom:'1rem' }}>
            {substitutes.map((sub, i) => {
              const p = selPortion[i] ?? sub.eqPortion;
              const factor = p / 100;
              const cal  = Math.round(sub.calorias_100g * factor);
              const diff = cal - Math.round(food.calorias);
              return (
                <div key={sub.id} style={S.subItem}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:600, fontSize:'0.88rem', color:'#2f2a26' }}>{emo(sub.nome)}</div>
                    <div style={{ fontSize:'0.75rem', color:'#736b64', marginTop:'2px' }}>
                      <input type="number" min="10" max="1000" value={p}
                        onChange={e => setSelPortion(prev => ({ ...prev, [i]: Math.max(10, parseInt(e.target.value) || 10) }))}
                        style={{ width:'52px', border:'1px solid #ebe3da', borderRadius:'4px',
                          padding:'1px 4px', fontSize:'0.75rem', marginRight:'3px' }} />g
                      {' · '}<span style={{ color:'#D4A574', fontWeight:600 }}>{cal} kcal</span>
                      {' · P '}{((sub.proteina_100g||0)*factor).toFixed(1)}g
                      {' · C '}{((sub.carbo_100g||0)*factor).toFixed(1)}g
                      {' · G '}{((sub.gordura_100g||0)*factor).toFixed(1)}g
                      <span style={{ marginLeft:'6px', fontWeight:600,
                        color: diff === 0 ? '#736b64' : diff < 0 ? '#46714f' : '#a8483f' }}>
                        {diff > 0 ? `+${diff}` : diff} kcal
                      </span>
                    </div>
                  </div>
                  <button style={S.subUseBtn}
                    onClick={() => onSelect(sub, p)}>
                    Usar
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <div style={{ display:'flex', gap:'0.5rem' }}>
          <button style={{ ...S.outlineBtn, flex:1 }} onClick={onBrowse}>
            <Ico e="🔍" sp />Buscar outro
          </button>
          <button style={{ ...S.outlineBtn, flex:1 }} onClick={onClose}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

